import { isAxiosError } from 'axios';
import type { ErrorEvent, EventHint } from '@sentry/vue';

/**
 * The path of a request with its ids taken out, so that `/api/live_track/12`
 * and `/api/live_track/34` are the same request.
 */
export function requestPathOf(url: string, baseURL?: string): string {
  const pathname = new URL(url, baseURL || 'http://localhost').pathname;
  return pathname
    .split('/')
    .map((segment) => {
      if (/^\d+$/.test(segment)) return ':id';
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) return ':uuid';
      return segment;
    })
    .join('/');
}

/**
 * Sentry's `beforeSend`. It files a failed API request under its method, path
 * and status code.
 *
 * Sentry groups errors by their stack trace. A failed request's stack trace is
 * only axios's own code, so every failed request went into one issue
 * (TABASCO-1V9): a 500, a 403 and a 401 from three different endpoints all
 * looked like the same error. With this, "403 on POST /api/location_zone/search"
 * is its own issue, and the issue's title names the request.
 */
export function groupRequestErrorsByRequest(event: ErrorEvent, hint: EventHint): ErrorEvent {
  const error = hint.originalException;
  if (!isAxiosError(error) || !error.config) return event;

  const method = (error.config.method ?? 'get').toUpperCase();
  const path = requestPathOf(error.config.url ?? '', error.config.baseURL);
  const outcome = error.response ? String(error.response.status) : (error.code ?? 'no response');
  event.fingerprint = ['axios-request-error', method, path, outcome];

  const exceptions = event.exception?.values ?? [];
  const exception = exceptions[exceptions.length - 1];
  if (exception) {
    exception.value = `${exception.value ?? error.message} (${method} ${path})`;
  }
  return event;
}
